export const GUIDES = [
  {
    slug: 'how-to-dispute-credit-report-error',
    title: 'How to Dispute a Credit Report Error (Step-by-Step Guide)',
    category: 'Getting Started',
    readTime: '7 min read',
    description:
      'A plain-English walkthrough of disputing credit report errors yourself — for free. Get your reports, spot inaccurate items, write an FCRA dispute letter, and track the 30-day clock.',
    sections: [
      {
        h: 'Step 1: Get your free credit reports',
        body: [
          'Federal law gives you free access to your credit report from each of the three major bureaus — Equifax, Experian, and TransUnion — at AnnualCreditReport.com. That is the only federally authorized source; you never need to pay for your own report.',
          'Download all three. Errors often appear on one bureau but not the others, so checking a single report is not enough.',
        ],
      },
      {
        h: 'Step 2: Look for inaccurate items',
        body: [
          'Go line by line through each tradeline (account) and check the details. The most common credit report errors include:',
          {
            list: [
              'Accounts that do not belong to you (mixed files or identity theft)',
              'Late payments you actually made on time',
              'Incorrect balances, credit limits, or account statuses',
              'Collections that were paid or settled but still show as open',
              'The same debt listed twice — often under two collection agencies',
              'Accounts that should have aged off (most negatives must be removed after 7 years)',
            ],
          },
          'Write down every item that looks wrong, on which report it appears, and why it is inaccurate. Facts are your leverage.',
        ],
      },
      {
        h: 'Step 3: Dispute in writing — not just online',
        body: [
          'Bureaus offer quick online dispute forms, but a written letter sent by mail creates a paper trail, lets you say exactly what is wrong in your own words, and preserves your full legal rights under the Fair Credit Reporting Act (FCRA).',
          'Your dispute letter should identify you (name, address, date of birth, last 4 of SSN), list each disputed item with the account number as shown on the report, state clearly why it is inaccurate, and request deletion or correction. Keep the tone factual — no templates full of legal bluster, no "credit sweep" tricks. Accurate, specific disputes are the ones that work.',
          'Selah Slate generates this letter for you automatically from your uploaded report — properly formatted for each bureau, with your items and reasons laid out cleanly.',
        ],
      },
      {
        h: 'Step 4: Mail it with proof',
        body: [
          'Send each letter by USPS Certified Mail with Return Receipt if you can. It costs a few dollars and gives you proof of the date the bureau received your dispute — which starts their legal clock.',
          'Include a copy (never the original) of a government ID and a utility bill or bank statement showing your current address.',
        ],
      },
      {
        h: 'Step 5: The 30-day clock',
        body: [
          'Under the FCRA, a bureau generally must investigate your dispute and respond within 30 days (up to 45 in limited cases). They must contact the company reporting the information (the furnisher), consider your evidence, and delete or correct anything that cannot be verified.',
          'Track the deadline for every letter you mail. Selah Slate does this automatically with a 35-day tracking window, so you know exactly when a bureau is out of time.',
        ],
      },
      {
        h: 'Step 6: Read the results — and know your next move',
        body: [
          'You will receive investigation results by mail or online. Three things can happen: the item is deleted (celebrate!), the item is updated (check the update is actually correct), or the bureau says it was "verified."',
          '"Verified" is not the end of the road. You can send a Method of Verification (MOV) letter demanding to know how it was verified, dispute directly with the furnisher, or file a complaint with the CFPB. See our guide on what to do when a bureau says verified.',
        ],
      },
    ],
  },
  {
    slug: 'sample-fcra-dispute-letter',
    title: 'Sample FCRA Dispute Letter (Free Template)',
    category: 'Templates',
    readTime: '5 min read',
    description:
      'See exactly what an effective FCRA dispute letter includes, where to mail it for Equifax, Experian, and TransUnion — and how to generate yours free inside Selah Slate.',
    sections: [
      {
        h: 'What makes a dispute letter effective',
        body: [
          'Bureaus process millions of disputes. Letters that get results are specific, factual, and easy to act on. That means: your full identifying information, the exact account name and number as shown on your report, a clear statement of what is wrong, and a direct request to delete or correct the item.',
          'Avoid internet "template blasts" that dispute everything as "not mine" — bureaus can flag those as frivolous and refuse to investigate. Selah Slate helps you separate the two paths clearly: items that look inaccurate (wrong balance, wrong dates, unrecognized accounts, mixed files, re-aged debt) become dispute letters, and accurate items you owe move into the built-in Debt Payoff Planner with a scripture-grounded repayment strategy. You keep the dignity of paying what you owe and the right to correct what is wrong.',
        ],
      },
      {
        h: 'What the letter looks like (preview)',
        body: [
          'Here is the shape of an effective dispute letter. The complete, personalized version is generated for you inside Selah Slate:',
          {
            letter:
              '[Your Full Name]\n[Street Address]\n[City, State ZIP]\n[Date]\n\n[Bureau Name]\n[Bureau Dispute Address]\n\nRe: Request for investigation of inaccurate information\n\nTo whom it may concern:\n\nI am writing to dispute the following information in my credit file. The items listed below are inaccurate…\n\n———  Preview only  ———\n\nThe full letter — each disputed item with the account number as shown\non your report, your specific dispute reasons, your evidence list, and\nthe legal closing bureaus expect — is generated for you inside Selah\nSlate, personalized and print-ready. Free forever.',
          },
          'Every letter Selah Slate creates lists your exact items and factual reasons, cites your rights under the FCRA, and is formatted separately for each bureau — ready to print, sign, and mail.',
        ],
      },
      {
        h: 'Where to mail it',
        body: [
          {
            list: [
              'Equifax — P.O. Box 740256, Atlanta, GA 30374-0256',
              'Experian — P.O. Box 4500, Allen, TX 75013',
              'TransUnion — P.O. Box 2000, Chester, PA 19016-2000',
            ],
          },
          'Send by Certified Mail with Return Receipt when possible, and keep a copy of everything you send.',
        ],
      },
      {
        h: 'Or skip the copy-paste entirely',
        body: [
          'Selah Slate builds this letter for you: upload your credit report, check the items you want to challenge, pick a factual reason for each, and download a print-ready letter per bureau — with the 30-day response clock tracked automatically. It is free, forever.',
        ],
      },
    ],
  },
  {
    slug: 'credit-bureau-says-verified',
    title: "Credit Bureau Says 'Verified'? Here's Your Next Move",
    category: 'Advanced',
    readTime: '6 min read',
    description:
      "A 'verified' dispute result isn't the end. Learn how a Method of Verification (MOV) letter, furnisher disputes, and a CFPB complaint keep the pressure on — legally and for free.",
    sections: [
      {
        h: 'What "verified" actually means',
        body: [
          'When a bureau responds that an item was "verified," it usually means the furnisher (the bank, lender, or collector reporting the debt) confirmed the data electronically — often through an automated system called e-OSCAR. It rarely means a human pulled original documents and checked your evidence.',
          'That is why "verified" is a checkpoint, not a verdict. The FCRA gives you tools to keep going.',
        ],
      },
      {
        h: 'Tool 1: The Method of Verification (MOV) letter',
        body: [
          'Under FCRA § 1681i(a)(7), you can ask the bureau to describe how it verified your dispute — including the business name, address, and phone number of the furnisher it contacted. Bureaus must provide this within 15 days of your request.',
          'An MOV letter signals that you know your rights and are documenting their process. If the "investigation" was a rubber stamp, the weaknesses start to show — and items sometimes get deleted on re-investigation.',
          'Selah Slate generates an MOV letter automatically when you log a "verified" response on any dispute.',
        ],
      },
      {
        h: 'Tool 2: Dispute directly with the furnisher',
        body: [
          'You can also dispute directly with the company reporting the information (FCRA § 1681s-2(b)). Write to the furnisher, identify the account, state the inaccuracy, and enclose your evidence. They must investigate and correct anything inaccurate with all bureaus they report to.',
        ],
      },
      {
        h: 'Tool 3: File a CFPB complaint',
        body: [
          'The Consumer Financial Protection Bureau (consumerfinance.gov/complaint) forwards your complaint to the bureau or furnisher, who must respond publicly — typically within 15 days. It is free, it creates a federal paper trail, and companies take it seriously.',
          'Attach your dispute letter, the bureau result, and your MOV request. Be factual and specific — the same rules that make dispute letters work apply here.',
        ],
      },
      {
        h: 'When to get help',
        body: [
          'If an item is genuinely inaccurate, you have disputed it properly, and it still will not budge — or your file shows signs of identity theft — consider a consumer-law attorney. Many take FCRA cases on contingency because the law allows recovery of damages and fees. Selah Slate is an educational tool, not a law firm, and this guide is not legal advice.',
        ],
      },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
